/**
 * v3.18-1295 — Communication Contact Directory API helper.
 *
 * Thin wrapper around the /api/communication-contacts and
 * /api/communication-contact-groups routes. Every consumer (Contact
 * Directory page, Notification Center, Client Communications tab,
 * Compose & Approve) reads through this file so the API surface
 * stays consistent.
 */
import api from './api';

const BASE = '/communication-contacts';
const GROUPS = '/communication-contact-groups';

export async function listContacts(params = {}) {
  const { data } = await api.get(BASE, { params });
  return data;
}

export async function getContact(id) {
  const { data } = await api.get(`${BASE}/${id}`);
  return data;
}

export async function createContact(payload) {
  const { data } = await api.post(BASE, payload);
  return data;
}

export async function updateContact(id, payload) {
  const { data } = await api.put(`${BASE}/${id}`, payload);
  return data;
}

export async function deleteContact(id) {
  await api.delete(`${BASE}/${id}`);
}

export async function reorderContacts(orderedIds) {
  const { data } = await api.post(`${BASE}/reorder`, { ordered_ids: orderedIds });
  return data;
}

export async function contactHistory(id, limit = 100) {
  const { data } = await api.get(`${BASE}/${id}/history`, { params: { limit } });
  return data;
}

export async function listGroups(params = {}) {
  const { data } = await api.get(GROUPS, { params });
  return data;
}

export async function createGroup(payload) {
  const { data } = await api.post(GROUPS, payload);
  return data;
}

export async function updateGroup(id, payload) {
  const { data } = await api.put(`${GROUPS}/${id}`, payload);
  return data;
}

export async function deleteGroup(id) {
  await api.delete(`${GROUPS}/${id}`);
}

export const PHONE_LABELS = ['mobile', 'office', 'home', 'extension', 'whatsapp', 'fax', 'other'];
export const EMAIL_LABELS = ['work', 'personal', 'billing', 'other'];

export function primaryPhone(contact) {
  if (!contact?.phones?.length) return null;
  const preferred = contact.phones.find((p) => p.is_preferred && !p.do_not_call);
  if (preferred) return preferred;
  const primary = contact.phones.find((p) => p.is_primary && !p.do_not_call);
  if (primary) return primary;
  return contact.phones.find((p) => !p.do_not_call) || contact.phones[0];
}

export function primaryEmail(contact) {
  if (!contact?.emails?.length) return null;
  return contact.emails.find((e) => e.is_primary) || contact.emails[0];
}

const RTF = (typeof Intl !== 'undefined' && Intl.RelativeTimeFormat)
  ? new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  : null;

/** Humanized "3 hours ago" for an ISO timestamp. Returns '' if empty. */
export function humanizeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!t || Number.isNaN(t)) return '';
  const diffMs = t - Date.now();
  const abs = Math.abs(diffMs);
  const table = [
    [60_000, 'second', 1_000],
    [3_600_000, 'minute', 60_000],
    [86_400_000, 'hour', 3_600_000],
    [604_800_000, 'day', 86_400_000],
    [2_629_800_000, 'week', 604_800_000],
    [31_557_600_000, 'month', 2_629_800_000],
    [Infinity, 'year', 31_557_600_000],
  ];
  for (const [max, unit, base] of table) {
    if (abs < max) {
      const val = Math.round(diffMs / base);
      return RTF ? RTF.format(val, unit) : `${Math.abs(val)} ${unit}s ago`;
    }
  }
  return '';
}
